import { TAB_NAMES } from '../../utils/accessMapConstants';
import { getNonDeletedStatuses } from '../constants/itemConstants';
import { PROJECT_STATUSES, COLLECTION_STATUSES } from '../constants/itemConstants';
import {
  SEARCH_RECORD_TYPE,
  SEARCH_VISIBILITY,
  DATE_PRESET_LABELS,
  SUPPORTED_ROLE_VALUES,
} from './searchPrefsConstants';
import { datePresetToFilterDates, detectDatePreset } from './searchPrefsDateUtils';
import { isPresetDefault } from './searchPrefsNormalize';

export function activeTabToVisibility(activeTab) {
  return activeTab === TAB_NAMES.DISCOVERY
    ? SEARCH_VISIBILITY.PUBLIC_PROJECTS
    : SEARCH_VISIBILITY.MY_PROJECTS;
}

export function visibilityToActiveTab(visibility, recordType) {
  if (visibility === SEARCH_VISIBILITY.PUBLIC_PROJECTS) {
    return TAB_NAMES.DISCOVERY;
  }
  return recordType === SEARCH_RECORD_TYPE.COLLECTION
    ? TAB_NAMES.MY_COLLECTIONS
    : TAB_NAMES.MY_PROJECTS;
}

export function getAllStatusesForRecordType(recordType) {
  const constants =
    recordType === SEARCH_RECORD_TYPE.COLLECTION ? COLLECTION_STATUSES : PROJECT_STATUSES;
  return getNonDeletedStatuses(constants);
}

/**
 * Build a SearchConfig payload from current filter state (backend-supported fields only).
 */
export function buildSearchConfigFromFilterState(
  filterState,
  { name, isDefault, recordType, editingPresetId = null }
) {
  const allStatuses = getAllStatusesForRecordType(recordType);
  const selectedStatuses = filterState.selectedStatuses || [];
  const allSelected =
    allStatuses.length > 0 &&
    selectedStatuses.length === allStatuses.length &&
    allStatuses.every((status) => selectedStatuses.includes(status));

  const roleSet = filterState.appliedRoleFilter;
  const hasRoleFilter = Array.isArray(roleSet) && roleSet.length > 0;

  const datePreset = detectDatePreset(
    filterState.dateFilterMode,
    filterState.queryFromDay,
    filterState.queryToDay
  );

  return {
    id: editingPresetId || undefined,
    name: name?.trim(),
    type: recordType,
    isDefault: Boolean(isDefault),
    visibility: activeTabToVisibility(filterState.activeTab),
    recordStatusFilter: allSelected ? null : [...selectedStatuses],
    recordPublicFilter: hasRoleFilter ? [...roleSet] : null,
    datePreset: datePreset || null,
  };
}

/**
 * Reset filters that are not stored in SearchConfig before applying a preset.
 */
export function resetUnsupportedFilterFields(actions) {
  actions.setSearchByKeyword?.('');
  actions.setSearchByTitle?.('');
  actions.setSearchByDescription?.('');
  actions.setSearchByTag?.('');
  actions.setSelectedTags?.([]);
  actions.setDateFilterMode?.('any');
  actions.setQueryFromDay?.('');
  actions.setQueryToDay?.('');
  actions.setIsDateFilterActive?.(false);
  actions.setIncludeMyPublic?.(true);
  actions.clearRatingFilter?.();
  actions.setNoRatingFilterApplied?.(true);
}

/**
 * Apply a SearchConfig onto filter context via action callbacks.
 */
export function applySearchConfigToFilters(searchConfig, actions, recordType) {
  const allStatuses = getAllStatusesForRecordType(recordType);

  resetUnsupportedFilterFields(actions);

  actions.setActiveTab?.(visibilityToActiveTab(searchConfig.visibility, recordType));

  const statuses = Array.isArray(searchConfig.recordStatusFilter)
    ? searchConfig.recordStatusFilter.filter(Boolean)
    : [];
  actions.setSelectedStatuses?.(
    statuses.length > 0 ? [...statuses] : [...allStatuses]
  );

  const roles = Array.isArray(searchConfig.recordPublicFilter)
    ? searchConfig.recordPublicFilter.filter((role) => SUPPORTED_ROLE_VALUES.includes(role))
    : [];

  if (actions.applyRolePreset) {
    actions.applyRolePreset(roles);
  } else if (roles.length === 0) {
    actions.clearRoleFilters?.();
  }

  const dates = datePresetToFilterDates(searchConfig.datePreset);
  actions.setDateFilterMode?.(dates.dateFilterMode);
  actions.setQueryFromDay?.(dates.queryFromDay);
  actions.setQueryToDay?.(dates.queryToDay);
  if (dates.queryFromDay || dates.queryToDay) {
    actions.setIsDateFilterActive?.(true);
  }
}

function formatStatusSummary(statuses, recordType) {
  const allStatuses = getAllStatusesForRecordType(recordType);
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return 'All statuses';
  }
  if (statuses.length === allStatuses.length) {
    return 'All statuses';
  }
  if (statuses.length <= 2) {
    return statuses.join(', ');
  }
  return `${statuses.length} statuses`;
}

function formatRoleSummary(roles) {
  if (!Array.isArray(roles) || roles.length === 0) {
    return 'Any role';
  }
  return roles.join(', ');
}

function formatVisibilitySummary(visibility, recordType) {
  if (visibility === SEARCH_VISIBILITY.PUBLIC_PROJECTS) {
    return 'Discovery';
  }
  return recordType === SEARCH_RECORD_TYPE.COLLECTION ? 'My Collections' : 'My Projects';
}

/**
 * Human-readable one-line summary for UI.
 */
export function summarizeSearchConfig(searchConfig, recordType) {
  if (!searchConfig) return '';

  const parts = [
    formatVisibilitySummary(searchConfig.visibility, recordType),
    formatStatusSummary(searchConfig.recordStatusFilter, recordType),
    formatRoleSummary(searchConfig.recordPublicFilter),
  ];

  if (searchConfig.datePreset && DATE_PRESET_LABELS[searchConfig.datePreset]) {
    parts.push(DATE_PRESET_LABELS[searchConfig.datePreset]);
  } else {
    parts.push('Any date');
  }

  return parts.join(' · ');
}

export function filterPresetsForRecordType(presets, recordType) {
  return (presets || []).filter((preset) => preset?.type === recordType);
}

/** Presets whose saved tab/visibility matches the currently active list tab. */
export function filterPresetsForActiveTab(presets, recordType, activeTab) {
  return filterPresetsForRecordType(presets, recordType).filter((preset) => {
    const presetTab = visibilityToActiveTab(preset.visibility, recordType);
    return presetTab === activeTab;
  });
}

export function findDefaultPreset(presets, recordType) {
  return (
    filterPresetsForRecordType(presets, recordType).find((preset) => isPresetDefault(preset)) ||
    null
  );
}
